import {
  useLocation as useTanStackLocation,
  useNavigate,
} from "@tanstack/react-router";
import { useCallback } from "react";

import {
  COMMENT_ANCHOR_PREFIX,
  COMMENT_ANCHOR_PREFIX_WITHOUT_HASH,
} from "@/react/components/CommentsCompass/constants";
import {
  useIsPostModalOverlay,
  usePostModalParams,
} from "@/react/hooks/usePostModalParams";
import { buildLocationHref } from "@/react/utils/router/buildLocationHref";
import { withLeadingHashPrefix } from "@/react/utils/router/locationHash";

import { useCommentsConfig } from "./ConfigContext";

export interface CommentLinkProps {
  hash?: string;
  rel?: string;
  target?: string;
  to: string;
}

const COMMENT_ID_PATTERN = /^\d+$/;

const buildCommentAnchor = (commentId: number): string =>
  `${COMMENT_ANCHOR_PREFIX_WITHOUT_HASH}${commentId}`;

const parseCommentIdFromAnchor = (anchor: string): number | null => {
  if (!anchor.startsWith(COMMENT_ANCHOR_PREFIX)) return null;
  const raw = anchor.slice(COMMENT_ANCHOR_PREFIX.length);
  return COMMENT_ID_PATTERN.test(raw) ? Number(raw) : null;
};

export interface CommentsDeepLink {
  deepLinkedCommentId: number | null;
  getCommentLinkProps: (commentId: number) => CommentLinkProps;
  getCommentUrl: (commentId: number) => string;
  showAllComments: () => void;
}

export const useCommentsDeepLink = (): CommentsDeepLink => {
  const navigate = useNavigate();
  const isPostModalOverlay = useIsPostModalOverlay();
  const { spaceSlug, postSlug } = usePostModalParams();
  const maskedHash = useTanStackLocation({
    select: loc => loc.maskedLocation?.hash ?? "",
  });
  const unmaskedHash = useTanStackLocation({
    select: loc => loc.hash ?? "",
  });
  const { isEmbedded, path } = useCommentsConfig();

  // We use the masked hash only when the post modal is open to ensure the background route doesn't react to the hash change.
  const hash = withLeadingHashPrefix(
    isPostModalOverlay ? maskedHash : unmaskedHash,
  );
  const deepLinkedCommentId = parseCommentIdFromAnchor(hash);

  const getCommentLinkProps = useCallback(
    (commentId: number): CommentLinkProps => ({
      to: path,
      hash: buildCommentAnchor(commentId),
      target: isEmbedded ? "_blank" : undefined,
      rel: isEmbedded ? "noreferrer" : undefined,
    }),
    [path, isEmbedded],
  );

  const getCommentUrl = useCallback(
    (commentId: number): string =>
      new URL(
        buildLocationHref({
          pathname: path,
          hash: buildCommentAnchor(commentId),
        }),
        window.location.origin,
      ).href,
    [path],
  );

  const showAllComments = useCallback(() => {
    if (isPostModalOverlay && spaceSlug && postSlug) {
      void navigate({
        replace: true,
        resetScroll: false,
        // TanStack skips history.replace when the background href and
        // state are unchanged. In a post modal the background route stays on e.g.
        // /feed while the comment hash lives on maskedLocation, so clearing
        // mask.hash alone is not enough to commit. Bumping internal state forces
        // the masked URL update. Same idea as useReplaceLocationState.
        state: prev => ({ ...prev, __commentsAllView: Date.now() }),
        mask: {
          to: "/c/$spaceSlug/$postSlug",
          params: { spaceSlug, postSlug },
          hash: "",
          unmaskOnReload: true,
        },
      });
      return;
    }

    void navigate({
      replace: true,
      hash: "",
    });
  }, [isPostModalOverlay, navigate, postSlug, spaceSlug]);

  return {
    deepLinkedCommentId,
    getCommentLinkProps,
    getCommentUrl,
    showAllComments,
  };
};
